'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type DialogKind = 'confirm' | 'prompt' | 'alert'
type DialogRequest = {
  id: number
  kind: DialogKind
  title: string
  message?: string
  defaultValue?: string
  inputType?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  resolve: (value: boolean | string | null) => void
}

type DialogApi = {
  confirm: (options: Omit<DialogRequest, 'id' | 'kind' | 'resolve'>) => Promise<boolean>
  prompt: (options: Omit<DialogRequest, 'id' | 'kind' | 'resolve'>) => Promise<string | null>
  alert: (options: Omit<DialogRequest, 'id' | 'kind' | 'resolve'>) => Promise<void>
}

const DialogContext = createContext<DialogApi | null>(null)
let dispatcher: ((request: Omit<DialogRequest, 'id' | 'resolve'>) => Promise<boolean | string | null>) | null = null

function runDialog(request: Omit<DialogRequest, 'id' | 'resolve'>): Promise<boolean | string | null> {
  if (!dispatcher) {
    console.warn('[standard-dialog] provider is not mounted', request)
    if (request.kind === 'confirm') return Promise.resolve(false)
    if (request.kind === 'prompt') return Promise.resolve(null)
    return Promise.resolve(true)
  }
  return dispatcher(request)
}

export const dialog: DialogApi = {
  confirm: (options) => runDialog({ ...options, kind: 'confirm' }) as Promise<boolean>,
  prompt: (options) => runDialog({ ...options, kind: 'prompt' }) as Promise<string | null>,
  alert: async (options) => { await runDialog({ ...options, kind: 'alert' }) },
}

export function StandardDialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<DialogRequest[]>([])
  const [inputValue, setInputValue] = useState('')
  const nextIdRef = useRef(1)
  const active = queue[0]

  const enqueue = useCallback((request: Omit<DialogRequest, 'id' | 'resolve'>) => {
    return new Promise<boolean | string | null>((resolve) => {
      setQueue((current) => [...current, { ...request, id: nextIdRef.current++, resolve }])
    })
  }, [])

  useEffect(() => {
    dispatcher = enqueue
    return () => {
      if (dispatcher === enqueue) dispatcher = null
    }
  }, [enqueue])

  useEffect(() => {
    setInputValue(active?.defaultValue || '')
  }, [active?.id, active?.defaultValue])

  const close = useCallback((value: boolean | string | null) => {
    setQueue((current) => {
      const [head, ...rest] = current
      head?.resolve(value)
      return rest
    })
  }, [])

  const api: DialogApi = dialog

  return (
    <DialogContext.Provider value={api}>
      {children}
      {active ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="standard-dialog-title"
          className="fixed inset-0 z-overlay bg-black/35 flex items-center justify-center px-4"
          onClick={() => close(active.kind === 'confirm' ? false : null)}
        >
          <form
            className="w-full max-w-md rounded-2xl border border-hair bg-white p-6 shadow-card"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault()
              close(active.kind === 'prompt' ? inputValue : true)
            }}
          >
            <h3 id="standard-dialog-title" className="text-base font-semibold text-ink">{active.title}</h3>
            {active.message ? <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-soft">{active.message}</p> : null}
            {active.kind === 'prompt' ? (
              <input
                className="input mt-4"
                type={active.inputType || 'text'}
                value={inputValue}
                autoFocus
                onChange={(event) => setInputValue(event.target.value)}
              />
            ) : null}
            <div className="mt-6 flex justify-end gap-3">
              {active.kind !== 'alert' ? (
                <button type="button" className="btn-secondary" onClick={() => close(active.kind === 'confirm' ? false : null)}>
                  {active.cancelText || '取消'}
                </button>
              ) : null}
              <button
                type="submit"
                className={active.danger ? 'inline-flex items-center justify-center rounded px-5 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600' : 'btn-primary'}
              >
                {active.confirmText || (active.kind === 'alert' ? '知道了' : '确定')}
              </button>
            </div>
          </form>
        </div>,
        document.body,
      ) : null}
    </DialogContext.Provider>
  )
}

export function useStandardDialog(): DialogApi {
  const ctx = useContext(DialogContext)
  if (!ctx) throw new Error('useStandardDialog must be used within StandardDialogProvider')
  return ctx
}
